import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useState } from "react";
import {
  Form,
  required,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmDataProvider } from "../providers/types";
import {
  dismissAdvisorCodePrompt,
  isAdvisorCodePromptDismissed,
} from "./advisorCodePromptDismissal";
import { getSalesErrorNotification } from "./salesErrorNotification";

/**
 * Almikia Epic E3b (TASK-067): right after sign-in, an asesora with no
 * clave (advisor_code) is asked for it, because a clave-less asesora can
 * never receive a lead (TASK-062 blocks it at the database level). Gated
 * purely off the cached identity (`role`/`advisor_code`, added to
 * `getIdentity()` by TASK-067) so it never issues an extra request for
 * users who are not clave-less asesoras.
 *
 * ASSUMPTION (ticket-documented): she may postpone the prompt ("Ahora no"),
 * and it asks again at her next sign-in while the clave is still empty --
 * it is deliberately not a blocking gate. A hard requirement would lock the
 * ~18 existing clave-less asesoras out of their work until the owner's
 * clave list (pending item P1) arrives.
 *
 * Rendered from both `Layout.tsx` (desktop) and `MobileLayout.tsx` (mobile)
 * so it shows up right after sign-in regardless of viewport.
 */
export const AdvisorCodePrompt = () => {
  const { identity, isPending, refetch: refetchIdentity } = useGetIdentity();
  const [dismissed, setDismissed] = useState(isAdvisorCodePromptDismissed);
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate, isPending: isSaving } = useMutation({
    mutationKey: ["advisor-code-prompt"],
    mutationFn: async (data: { advisor_code: string }) => {
      if (!identity) {
        throw new Error("Record not found");
      }
      return dataProvider.salesUpdate(identity.id, data);
    },
    onSuccess: () => {
      refetchIdentity();
      notify("crm.advisorCodePrompt.saved", {
        messageArgs: {
          _: "Your advisor code has been saved",
        },
      });
    },
    onError: (error) => {
      const { message, args } = getSalesErrorNotification(error);
      notify(message, {
        type: "error",
        messageArgs: { ...args, _: "An error occurred. Please try again" },
      });
    },
  });

  const handlePostpone = () => {
    dismissAdvisorCodePrompt();
    setDismissed(true);
  };

  // Nothing to decide until the (cached) identity resolves -- avoids a
  // flash of the dialog for users who turn out not to need it.
  if (isPending || !identity || dismissed) return null;
  if (identity.role !== "asesora") return null;
  if (identity.advisor_code) return null;

  const handleOnSubmit = (values: Record<string, unknown>) => {
    mutate({ advisor_code: values.advisor_code as string });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // Closing without saving (Escape, backdrop click, the header "X")
        // is treated the same as an explicit "Ahora no": it postpones, it
        // never silently disappears for good.
        if (!open) handlePostpone();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <Form onSubmit={handleOnSubmit}>
          <DialogHeader>
            <DialogTitle>
              {translate("crm.advisorCodePrompt.title", {
                _: "Add your advisor code",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("crm.advisorCodePrompt.description", {
                _: "You need your advisor code to receive leads. Add it whenever you have it; you can also do it later from your profile.",
              })}
            </DialogDescription>
          </DialogHeader>
          <TextInput
            source="advisor_code"
            label="resources.sales.fields.advisor_code"
            validate={required("crm.advisorCodePrompt.required")}
            helperText={false}
            autoFocus
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handlePostpone}>
              {translate("crm.advisorCodePrompt.postpone", {
                _: "Not now",
              })}
            </Button>
            {/* design-system.md section 6: the gradient primary style is
                reserved for a screen's single main action. */}
            <Button type="submit" variant="gradient" disabled={isSaving}>
              <Save />
              {translate("ra.action.save")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
